import React, { useState, useEffect } from "react";
import { userService } from "../../../_services";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Loader2 } from "lucide-react";

/**
 * Edit Referral User Component
 * Modernized with shadcn components - No Dialog wrapper, parent handles Dialog
 */

const US_STATES = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
    'Wisconsin', 'Wyoming'
];

function Edit_user_pop({ repid, toggle_add_edit_popup }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        userName: '',
        fullName: '',
        phone: '',
        email: '',
        groupNumber: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'United States'
    });
    const [errors, setErrors] = useState({});
    const [submitMessage, setSubmitMessage] = useState('');

    useEffect(() => {
        fetchUserDetails();
    }, [repid]);

    const fetchUserDetails = async () => {
        setLoading(true);
        try {
            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = Usermeta_Data.split(':');
            
            const apidata = await userService.DetailOfUser(userId, userName, repid);
            
            if (apidata && apidata.length > 0) {
                const user = apidata[0];
                setFormData({
                    userName: user.userName || '',
                    fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
                    phone: user.phone || '',
                    email: user.email || '',
                    groupNumber: user.groupNumber || '',
                    address: user.address1 || '',
                    city: user.cityName || '',
                    state: user.stateName || '',
                    zipCode: user.zipCode || '',
                    country: user.countryName || 'United States'
                });
            }
        } catch (error) {
            console.error('Error fetching user details:', error);
            setSubmitMessage('Failed to load user details');
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (formData.userName.length < 3) {
            newErrors.userName = 'Username must be at least 3 characters long';
        }
        if (formData.fullName.length < 3) {
            newErrors.fullName = 'Full name must be at least 3 characters long';
        }
        const phoneDigits = formData.phone.replace(/[^\d]/g, '');
        if (phoneDigits.length !== 10) {
            newErrors.phone = 'Phone number must be 10 digits';
        }
        if (formData.address.length < 1) {
            newErrors.address = 'Please enter street address';
        }
        if (formData.city.length < 2) {
            newErrors.city = 'Please enter city';
        }
        if (!formData.state || formData.state === '') {
            newErrors.state = 'Please select state';
        }
        if (formData.zipCode.length < 5) {
            newErrors.zipCode = 'Please enter valid zip code';
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitMessage('');

        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setSaving(true);
        try {
            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = Usermeta_Data.split(':');
            
            const apidata = await userService.UpdateUserDetail(
                userName,
                userId,
                formData.userName,
                repid,
                formData.fullName,
                formData.email,
                formData.phone,
                formData.groupNumber,
                formData.address,
                '', // Address2
                formData.country,
                formData.state,
                formData.city,
                formData.zipCode
            );

            if (apidata && apidata.length > 0) {
                if (apidata[0].code === '1') {
                    toggle_add_edit_popup(e);
                } else {
                    setSubmitMessage(apidata[0].statusMessage || 'Failed to update user');
                }
            }
        } catch (error) {
            console.error('Error updating user:', error);
            setSubmitMessage('Failed to update user. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setErrors(prev => ({ ...prev, [field]: '' }));
        setSubmitMessage('');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Section */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-brand-dark border-b pb-2">Personal</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="userName">
                            Username <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="userName"
                            value={formData.userName}
                            onChange={(e) => handleChange('userName', e.target.value)}
                            className={errors.userName ? 'border-red-500' : ''}
                        />
                        {errors.userName && (
                            <p className="text-xs text-red-600">{errors.userName}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="fullName">
                            Full Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="fullName"
                            value={formData.fullName}
                            onChange={(e) => handleChange('fullName', e.target.value)}
                            className={errors.fullName ? 'border-red-500' : ''}
                        />
                        {errors.fullName && (
                            <p className="text-xs text-red-600">{errors.fullName}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="phone">
                            Phone <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => handleChange('phone', e.target.value)}
                            placeholder="(555) 555-5555"
                            className={errors.phone ? 'border-red-500' : ''}
                        />
                        {errors.phone && (
                            <p className="text-xs text-red-600">{errors.phone}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            disabled
                            className="bg-gray-50"
                        />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="groupNumber">Primary Group Number</Label>
                        <Input
                            id="groupNumber"
                            value={formData.groupNumber}
                            disabled
                            className="bg-gray-50"
                        />
                    </div>
                </div>
            </div>

            {/* Address Section */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-brand-dark border-b pb-2">Address</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="address">
                            Street Address <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="address"
                            value={formData.address}
                            onChange={(e) => handleChange('address', e.target.value)}
                            className={errors.address ? 'border-red-500' : ''}
                        />
                        {errors.address && (
                            <p className="text-xs text-red-600">{errors.address}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="country">Country</Label>
                        <Select value={formData.country} onValueChange={(value) => handleChange('country', value)}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="United States">United States</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="state">
                            State <span className="text-red-500">*</span>
                        </Label>
                        <Select 
                            value={formData.state} 
                            onValueChange={(value) => handleChange('state', value)}
                        >
                            <SelectTrigger className={errors.state ? 'border-red-500' : ''}>
                                <SelectValue placeholder="Select state" />
                            </SelectTrigger>
                            <SelectContent className="max-h-64">
                                {US_STATES.map((state) => (
                                    <SelectItem key={state} value={state}>
                                        {state}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {errors.state && (
                            <p className="text-xs text-red-600">{errors.state}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="zipCode">
                            ZIP / Postal Code <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="zipCode"
                            value={formData.zipCode}
                            onChange={(e) => handleChange('zipCode', e.target.value)}
                            maxLength={10}
                            className={errors.zipCode ? 'border-red-500' : ''}
                        />
                        {errors.zipCode && (
                            <p className="text-xs text-red-600">{errors.zipCode}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="city">
                            City <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="city"
                            value={formData.city}
                            onChange={(e) => handleChange('city', e.target.value)}
                            className={errors.city ? 'border-red-500' : ''}
                        />
                        {errors.city && (
                            <p className="text-xs text-red-600">{errors.city}</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Submit Message */}
            {submitMessage && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{submitMessage}</p>
                </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button
                    type="button"
                    variant="outline"
                    onClick={toggle_add_edit_popup}
                    disabled={saving}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="secondary"
                    disabled={saving}
                >
                    {saving ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Saving...
                        </>
                    ) : (
                        'Save Changes'
                    )}
                </Button>
            </div>
        </form>
    );
}

export default Edit_user_pop;
